/* /battery-checker — page layer.
 *
 * Two instruments on one page, and they do not depend on each other:
 *
 *   THE LIVE READING. What the Battery Status API says, read through the shared
 *   reader (battery-status.js: it owns the "fake 100 %" signature) and put on
 *   screen through BatteryLive, which owns what the four fields may be said to
 *   mean — "plugged in" is not "charging", an unknown time is not ∞, one step
 *   is not a rate. Every reading also goes through BatteryReport.falls(), so a
 *   level that sits at half and then drops is named when it happens, and the
 *   last reading is kept so that a visit after the laptop switched itself off
 *   can be compared with the one before it.
 *
 *   THE REPORT. The file Windows writes with `powercfg /batteryreport`, read
 *   by BatteryReport: wear, cycles, capacity week by week, and the falls its
 *   log holds. It needs no API, so it also works where the live reading does
 *   not (Firefox, Safari).
 *
 * Why a report reader on a page about the live level: bug report 2026-10-06
 * (/ar, Windows 10, Edge) — "the battery is at half and suddenly drops to
 * zero". The reading was real and the page was working; it had nothing to say
 * about it. docs/why/battery-checker.md.
 */
(function () {
    'use strict';

    var SLUG = 'battery-checker';
    var $ = function (id) { return document.getElementById(id); };
    var T = {};
    try { T = JSON.parse(($('bc-i18n') || {}).textContent || '{}'); } catch (e) { T = {}; }
    function t(key, vars) {
        var s = T[key] != null ? String(T[key]) : key, k;
        if (vars) for (k in vars) s = s.split(':' + k).join(vars[k]);
        return s;
    }
    var LOCALE = window.__LOCALE || document.documentElement.lang || 'en';

    /* The __ae* helpers are defined by the layout's analytics block, which runs
       AFTER page scripts: anything emitted at load would hit an undefined
       helper and vanish (how the always-100 % bug stayed invisible, 2026-06). */
    function whenAe(cb) {
        if (window.__aeDiagnostic) { try { cb(); } catch (e) {} return; }
        var n = 0;
        var iv = setInterval(function () {
            if (window.__aeDiagnostic || ++n > 60) { clearInterval(iv); if (window.__aeDiagnostic) { try { cb(); } catch (e) {} } }
        }, 50);
    }
    function diagnostic(p) { whenAe(function () { window.__aeDiagnostic(p); }); }
    function blocked(p) { whenAe(function () { if (window.__aeBlocked) window.__aeBlocked(p); }); }
    function funnel(stage, p) { whenAe(function () { if (window.__aeFunnel) window.__aeFunnel(stage, p); }); }

    function whenGlobal(name) {
        if (window[name]) return Promise.resolve(window[name]);
        if (window.__whenGlobal) return window.__whenGlobal(name);
        return Promise.reject(new Error(name));
    }

    /* ── words for numbers ────────────────────────────────────────────────── */

    /* Korean writes the unit against the number. */
    var GAP = /^ko/.test(LOCALE) ? '' : ' ';
    function span(seconds) {
        seconds = Math.max(0, Math.round(seconds));
        if (seconds < 90) return seconds + GAP + t('seconds_short');
        var m = Math.round(seconds / 60);
        if (m < 60) return m + GAP + t('minutes_short');
        var h = Math.floor(m / 60); m %= 60;
        if (h < 48) return h + GAP + t('hours_short') + (m ? ' ' + m + GAP + t('minutes_short') : '');
        return Math.round(h / 24) + GAP + t('days_short');
    }
    function clockTime(seconds) {
        var h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60);
        return h > 0 ? h + t('hours_short') + ' ' + m + t('minutes_short') : m + t('minutes_short');
    }
    function stopwatch(ms) {
        var s = Math.floor(ms / 1000), h = Math.floor(s / 3600); s %= 3600;
        var m = Math.floor(s / 60); s %= 60;
        var two = function (n) { return String(n).padStart(2, '0'); };
        return h > 0 ? h + ':' + two(m) + ':' + two(s) : m + ':' + two(s);
    }
    /* The report's times are the machine's own wall clock with no zone; they
       are printed as written, which is what UTC formatting of a naive stamp does. */
    function reportDay(ms) {
        try { return new Date(ms).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }); }
        catch (e) { return new Date(ms).toISOString().slice(0, 10); }
    }
    function reportClock(ms) {
        try { return new Date(ms).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }); }
        catch (e) { return new Date(ms).toISOString().slice(11, 16); }
    }
    function wh(mwh) { return (mwh / 1000).toFixed(1) + ' ' + t('unit_wh'); }

    /* ══ THE LIVE READING ═══════════════════════════════════════════════════ */

    function live() {
        var main = $('bcMain'), more = $('bcMore'), unsupportedBox = $('bcUnsupported');
        if (!main) return;

        function unsupported(reason) {
            main.classList.remove('show'); more.classList.remove('show');
            unsupportedBox.classList.add('show');
            blocked({ reason: reason });
        }
        if (!navigator.getBattery) { unsupported('no_battery_api'); return; }
        main.classList.add('show'); more.classList.add('show');

        var el = {
            body: $('bcBody'), fill: $('bcFill'), bolt: $('bcBolt'), pct: $('bcPercent'), dot: $('bcDot'),
            status: $('bcStatusText'), sStatus: $('bcStatStatus'), sLevel: $('bcStatLevel'),
            time: $('bcStatTime'), rate: $('bcStatRate'), timeLabel: $('bcTimeLabel'), rateLabel: $('bcRateLabel'),
            note: $('bcStatNote'), canvas: $('bcCanvas'), empty: $('bcGraphEmpty'), log: $('bcLogList'),
            tips: $('bcTipsList'), sessDur: $('bcSessionDur'), sessChg: $('bcSessionChange'),
            speedRow: $('bcSpeedRow'), speed: $('bcSpeed'), notice: $('bcDefaultNotice'),
            fall: $('bcFallNotice'), fallTitle: $('bcFallTitle'), fallDesc: $('bcFallDesc'),
            alertInput: $('bcAlertInput'), alertSet: $('bcAlertSet'), alertStatus: $('bcAlertStatus'),
            fs: $('bcFullscreen'), fsPct: $('bcFsPct'), fsStatus: $('bcFsStatus'), fsTime: $('bcFsTime'),
            fsBtn: $('bcFullscreenBtn'), fsClose: $('bcFsClose'),
        };

        var samples = [];            /* { t, pct, plugged } — BatteryLive's unit */
        var MAX_SAMPLES = 2880;      /* a day of half-minute readings */
        var startTime = Date.now(), startPct = null, battery = null;
        var alertLevel = null, alertFired = false;
        var apiProven = false, noticeShown = false, heldReported = false;
        var fallsSeen = {};
        var logged = 0;
        var isWindows = /Windows/i.test(navigator.userAgent);

        function color(pct) { return pct > 50 ? 'green' : pct > 20 ? 'yellow' : 'red'; }
        function clock() {
            var d = new Date(), two = function (n) { return String(n).padStart(2, '0'); };
            return two(d.getHours()) + ':' + two(d.getMinutes()) + ':' + two(d.getSeconds());
        }

        function addLog(msg) {
            if (!logged) el.log.textContent = '';
            logged++;
            var row = document.createElement('div'); row.className = 'bc-log-item';
            var when = document.createElement('span'); when.className = 'bc-log-time'; when.textContent = clock();
            var what = document.createElement('span'); what.className = 'bc-log-msg'; what.textContent = msg;
            row.appendChild(when); row.appendChild(what);
            el.log.appendChild(row);
            while (el.log.children.length > 50) el.log.removeChild(el.log.firstChild);
            el.log.scrollTop = el.log.scrollHeight;
        }

        /* A browser that will not report the battery keeps the spec's default
           slots, which read as a full, plugged-in pack. One read cannot tell
           that from a device that really is full, so the notice is worded
           conditionally and retracts itself on the first reading that is not
           the default. The signature lives in battery-status.js. */
        function reliability(b) {
            if (apiProven) return;
            var def = window.__batteryStatus ? window.__batteryStatus.isDefaultQuad(b)
                : (b.level === 1 && b.charging === true && b.chargingTime === 0 && !isFinite(b.dischargingTime));
            if (!def) {
                apiProven = true;
                if (noticeShown) { el.notice.hidden = true; noticeShown = false; }
                return;
            }
            if (!noticeShown) {
                noticeShown = true;
                el.notice.hidden = false;
                diagnostic({ issue: 'battery_default_emulation', ct: b.chargingTime, dt: isFinite(b.dischargingTime) ? b.dischargingTime : -1 });
            }
        }

        function sample() {
            var s = { t: Date.now(), pct: Math.round(battery.level * 100), plugged: !!battery.charging };
            samples.push(s);
            if (samples.length > MAX_SAMPLES) samples.shift();
            /* Kept for the next visit: if this one ends with the laptop switching
               itself off, the page that opens after it has something to compare
               its first reading with. */
            try { window.__toolSettings.save(SLUG, 'last', s); } catch (e) {}
            return s;
        }

        function announceFall(f, since) {
            var key = f.from + ':' + f.to;
            if (fallsSeen[key]) return;
            fallsSeen[key] = 1;
            el.fallTitle.textContent = since
                ? t('fall_since_title', { ago: span(since / 1000), from: f.fromPct, to: f.toPct })
                : t('fall_live_title', { from: f.fromPct, to: f.toPct, time: span(f.seconds) });
            el.fallDesc.textContent = t('fall_live_desc') + (isWindows ? ' ' + t('fall_live_desc_win') : '');
            el.fall.hidden = false;
            addLog('⚠️ ' + t('log_fall', { from: f.fromPct, to: f.toPct }));
            diagnostic({ issue: 'level_fall', src: since ? 'since_last' : 'live', drop: Math.round(f.dropPct), sec: Math.round(f.seconds) });
        }

        function watchForFalls() {
            if (!window.BatteryReport || !apiProven) return;
            var rows = samples.map(function (s) { return { t: s.t, pct: s.pct, ac: s.plugged }; });
            window.BatteryReport.falls(rows).forEach(function (f) { announceFall(f, 0); });
        }

        function tips(pct, plug, rate) {
            var list = [];
            if (pct <= 20 && plug === 'discharging') list.push({ text: t('tip_low_battery'), active: true, icon: '⚠️' });
            if (plug === 'charging') list.push({ text: t('tip_charging'), active: true, icon: '⚡' });
            if (plug === 'full') list.push({ text: t('tip_full'), active: true, icon: '🔌' });
            if (rate != null && rate <= -window.BatteryLive.C.FAST_DRAIN) list.push({ text: t('tip_fast_drain'), active: true, icon: '🔥' });
            ['tip_health_1', 'tip_health_2', 'tip_health_3', 'tip_health_4'].forEach(function (k) {
                list.push({ text: t(k), active: false, icon: '💡' });
            });
            el.tips.textContent = '';
            list.forEach(function (tip) {
                var row = document.createElement('div'); row.className = 'bc-tip-item' + (tip.active ? ' active' : '');
                var ic = document.createElement('span'); ic.className = 'bc-tip-icon'; ic.textContent = tip.icon;
                var tx = document.createElement('span'); tx.textContent = tip.text;
                row.appendChild(ic); row.appendChild(tx); el.tips.appendChild(row);
            });
        }

        function drawGraph() {
            if (samples.length < 2) { el.empty.classList.remove('hidden'); return; }
            el.empty.classList.add('hidden');
            var dpr = window.devicePixelRatio || 1;
            var rect = el.canvas.parentElement.getBoundingClientRect();
            var W = rect.width, H = rect.height;
            if (!(W > 0 && H > 0)) return;
            el.canvas.width = W * dpr; el.canvas.height = H * dpr;
            var ctx = el.canvas.getContext('2d'); ctx.scale(dpr, dpr);
            var pad = { top: 10, right: 12, bottom: 24, left: 36 };
            var gW = W - pad.left - pad.right, gH = H - pad.top - pad.bottom;
            var t0 = samples[0].t, t1 = samples[samples.length - 1].t, dt = t1 - t0 || 1, i, y;
            ctx.strokeStyle = 'rgba(128,128,128,.15)'; ctx.lineWidth = 1;
            ctx.font = '10px system-ui'; ctx.fillStyle = 'rgba(128,128,128,.7)'; ctx.textAlign = 'right';
            for (var p = 0; p <= 100; p += 25) {
                y = pad.top + gH - (p / 100) * gH;
                ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + gW, y); ctx.stroke();
                ctx.fillText(p + '%', pad.left - 4, y + 3);
            }
            ctx.textAlign = 'center';
            var labels = Math.min(5, samples.length);
            for (i = 0; i < labels; i++) {
                var idx = Math.floor(i * (samples.length - 1) / (labels - 1));
                var d = new Date(samples[idx].t);
                ctx.fillText(String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'),
                    pad.left + ((samples[idx].t - t0) / dt) * gW, H - 4);
            }
            var last = samples[samples.length - 1];
            var line = last.plugged ? '#6366f1' : (last.pct > 20 ? '#22c55e' : '#ef4444');
            var X = function (s) { return pad.left + ((s.t - t0) / dt) * gW; };
            var Y = function (s) { return pad.top + gH - (s.pct / 100) * gH; };
            ctx.beginPath(); ctx.strokeStyle = line; ctx.lineWidth = 2.5; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
            samples.forEach(function (s, k) { if (k) ctx.lineTo(X(s), Y(s)); else ctx.moveTo(X(s), Y(s)); });
            ctx.stroke();
            if (samples.length <= 120) {
                ctx.fillStyle = line;
                samples.forEach(function (s) { ctx.beginPath(); ctx.arc(X(s), Y(s), 3, 0, Math.PI * 2); ctx.fill(); });
            }
            ctx.globalAlpha = 0.08; ctx.beginPath();
            samples.forEach(function (s, k) { if (k) ctx.lineTo(X(s), Y(s)); else ctx.moveTo(X(s), Y(s)); });
            ctx.lineTo(pad.left + gW, pad.top + gH); ctx.lineTo(pad.left, pad.top + gH); ctx.closePath();
            ctx.fillStyle = line; ctx.fill(); ctx.globalAlpha = 1;
        }

        function sizeFill(pct) {
            var w = parseInt(getComputedStyle(el.body).width, 10) - 8;
            if (w > 0) el.fill.style.width = (Math.max(0, Math.min(100, pct)) / 100 * w) + 'px';
        }

        function checkAlert(pct) {
            if (alertLevel === null || alertFired) return;
            var hit = alertLevel <= 50 ? pct <= alertLevel : pct >= alertLevel;
            if (!hit) return;
            alertFired = true;
            var msg = t('alert_triggered', { level: alertLevel });
            addLog('🔔 ' + msg);
            el.alertStatus.textContent = '🔔 ' + msg;
            if (window.__toast) window.__toast(msg, 'warning');
            try { if (navigator.vibrate) navigator.vibrate([200, 100, 200]); } catch (e) {}
            try {
                var actx = new (window.AudioContext || window.webkitAudioContext)();
                var osc = actx.createOscillator(); osc.frequency.value = 800;
                var gain = actx.createGain(); gain.gain.value = 0.3;
                osc.connect(gain); gain.connect(actx.destination);
                osc.start(); osc.stop(actx.currentTime + 0.5);
            } catch (e) {}
        }

        function render() {
            var L = window.BatteryLive, now = Date.now();
            var cur = samples[samples.length - 1], pct = cur.pct, c = color(pct);
            var plug = L.plug(samples, now);
            var rate = L.rate(samples, window.ChargerTestCore);
            var plugged = cur.plugged;

            sizeFill(pct);
            el.fill.className = 'bc-battery-fill ' + c;
            el.bolt.classList.toggle('show', plugged);
            el.body.classList.toggle('charging', plug === 'charging');
            el.pct.textContent = pct + '%'; el.pct.className = 'bc-percent ' + c;

            var status = plug === 'discharging' ? t('discharging')
                : plug === 'full' ? t('full') + ' · ' + t('plugged_in')
                : plug === 'charging' ? t('charging')
                : plug === 'held' ? t('plugged_in') + ' · ' + t('not_rising')
                : t('plugged_in');
            el.status.textContent = status;
            el.status.setAttribute('data-plug', plug);
            el.dot.className = 'bc-status-dot ' + (plug === 'discharging' ? 'discharging' : plug === 'full' ? 'full' : plug === 'held' ? 'held' : 'charging');
            el.sStatus.textContent = status; el.sLevel.textContent = pct + '%';

            /* Time: the system's figure when it gives one; on battery, failing
               that, the page's own from the measured rate; otherwise a dash and
               a line saying why — never the ∞ the API uses for "not known". */
            var sys = L.reported(plugged ? battery.chargingTime : battery.dischargingTime);
            var own = (!plugged && sys == null) ? L.secondsToEmpty(pct, rate) : null;
            var timeText = sys != null ? clockTime(sys) : own != null ? '≈ ' + clockTime(own) : '—';
            el.time.textContent = timeText;
            el.timeLabel.textContent = plugged ? t('time_label_charging') : t('time_label_discharging');
            el.rateLabel.textContent = plugged ? t('rate_label_charging') : t('rate_label_discharging');
            el.rate.textContent = rate != null ? (rate > 0 ? '+' : '−') + Math.abs(rate).toFixed(1) + t('per_hour') : '—';

            var note = '';
            if (plug === 'held') {
                var r = L.run(samples), since = r[0].t, i;
                for (i = 1; i < r.length; i++) if (r[i].pct > r[i - 1].pct) since = r[i].t;
                note = t('held_note', { min: Math.floor((now - since) / 60000) });
                if (!heldReported) { heldReported = true; diagnostic({ issue: 'plug_held', pct: pct }); }
            } else if (plug === 'charging' || plug === 'plugged') {
                note = sys == null ? t('time_unknown_note') : (rate == null ? t('rate_wait_note') : '');
            } else if (plug === 'discharging') {
                note = (sys == null && own == null) ? t('time_measuring_note') : (rate == null ? t('rate_wait_note') : '');
            }
            el.note.textContent = note; el.note.hidden = !note;

            el.sessDur.textContent = stopwatch(now - startTime);
            var delta = pct - startPct;
            el.sessChg.textContent = delta > 0 ? t('session_gained', { value: delta })
                : delta < 0 ? t('session_lost', { value: -delta }) : t('session_stable');

            /* Charging speed: from the system's time to full where there is one,
               from the measured rate where there is not. */
            var perHour = (plugged && sys != null && pct < 100) ? (100 - pct) / (sys / 3600) : (plugged && rate > 0 ? rate : null);
            if (perHour != null) {
                el.speedRow.classList.remove('hidden');
                el.speed.textContent = perHour > 40 ? t('speed_fast') : perHour > 15 ? t('speed_normal') : t('speed_slow');
            } else {
                el.speedRow.classList.add('hidden');
            }

            el.fsPct.textContent = pct + '%'; el.fsPct.className = 'bc-fs-pct ' + c;
            el.fsStatus.textContent = status;
            el.fsTime.textContent = (sys != null || own != null) ? timeText + ' ' + (plugged ? t('time_to_full') : t('time_to_empty')) : '';

            checkAlert(pct);
            tips(pct, plug, rate);
            reliability(battery);
            watchForFalls();
        }

        function tick() { sample(); render(); drawGraph(); }

        el.alertSet.addEventListener('click', function () {
            if (alertLevel !== null) {
                alertLevel = null; alertFired = false;
                el.alertSet.textContent = t('alert_set');
                el.alertStatus.classList.remove('show');
                el.alertInput.disabled = false;
                return;
            }
            var val = parseInt(el.alertInput.value, 10);
            if (!val || val < 1 || val > 100) return;
            alertLevel = val; alertFired = false;
            el.alertSet.textContent = t('alert_cancel');
            el.alertStatus.textContent = t('alert_active', { level: val });
            el.alertStatus.classList.add('show');
            el.alertInput.disabled = true;
        });

        /* Fullscreen: the big number is a fixed overlay either way; the API only
           takes the browser chrome away, so a refusal leaves the overlay up. */
        function closeFs() {
            el.fs.classList.remove('show');
            document.body.style.overflow = '';
            try { if (document.fullscreenElement) document.exitFullscreen().catch(function () {}); } catch (e) {}
        }
        el.fsBtn.addEventListener('click', function () {
            el.fs.classList.add('show');
            document.body.style.overflow = 'hidden';
            if (window.__safeFullscreen) window.__safeFullscreen(el.fs);
        });
        el.fsClose.addEventListener('click', closeFs);
        document.addEventListener('fullscreenchange', function () {
            if (!document.fullscreenElement && el.fs.classList.contains('show')) closeFs();
        });
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && el.fs.classList.contains('show')) closeFs();
        });

        window.addEventListener('resize', function () {
            if (!samples.length) return;
            sizeFill(samples[samples.length - 1].pct); drawGraph();
        });

        function depMissing(dep) {
            unsupportedBox.textContent = '';
            var p = document.createElement('p'); p.textContent = t('core_script_failed');
            unsupportedBox.appendChild(p);
            main.classList.remove('show'); more.classList.remove('show');
            unsupportedBox.classList.add('show');
            diagnostic({ issue: 'dep_missing', dep: dep });
        }

        /* Every script this needs is its own request and can fail to arrive;
           wait for each with a bound and say so in the panel, not in a
           "Cannot read properties of undefined" (Sentry, 2026-10-02). */
        Promise.all([whenGlobal('__batteryStatus'), whenGlobal('BatteryLive'), whenGlobal('ChargerTestCore'), whenGlobal('BatteryReport')])
            .then(function (deps) { return deps[0].read(); }, function (e) { depMissing((e && e.message) || 'battery'); return null; })
            .then(function (reading) {
                if (!reading) return;
                if (reading.state === 'unsupported') {
                    unsupported(reading.reason === 'rejected' ? 'getbattery_rejected' : 'no_battery_api');
                    return;
                }
                battery = reading.manager;
                whenAe(function () {
                    if (window.__aeToolComplete) window.__aeToolComplete({ suspect_default: reading.state === 'unconfirmed' ? 1 : 0 });
                });

                var prev = null;
                try { prev = window.__toolSettings.load(SLUG, 'last', null); } catch (e) {}
                var first = sample();
                startPct = first.pct;
                render(); drawGraph();
                addLog(t('log_session_start', { level: first.pct }));

                /* The last visit's final reading and this visit's first are two
                   readings like any other pair. */
                if (apiProven && prev && prev.plugged === false && typeof prev.t === 'number' && typeof prev.pct === 'number' && prev.t < first.t) {
                    var f = window.BatteryReport.falls([{ t: prev.t, pct: prev.pct, ac: false }, { t: first.t, pct: first.pct, ac: first.plugged }]);
                    if (f.length) announceFall(f[0], first.t - prev.t);
                }

                /* Some browsers hand back a manager without listeners. */
                if (typeof battery.addEventListener === 'function') {
                    battery.addEventListener('levelchange', function () {
                        addLog(t('log_level_change', { level: Math.round(battery.level * 100) }));
                        if (battery.level >= 1 && battery.charging) addLog(t('log_full'));
                        tick();
                    });
                    battery.addEventListener('chargingchange', function () {
                        addLog(battery.charging ? t('log_charging') : t('log_discharging'));
                        tick();
                    });
                    battery.addEventListener('chargingtimechange', render);
                    battery.addEventListener('dischargingtimechange', render);
                }
                setInterval(tick, 30000);
                setInterval(function () { el.sessDur.textContent = stopwatch(Date.now() - startTime); }, 1000);
            });
    }

    /* ══ THE REPORT ═════════════════════════════════════════════════════════ */

    function report() {
        var card = $('bcHealth'), drop = $('bcReportDrop');
        if (!card || !drop) return;

        var ua = navigator.userAgent || '';
        var os = /Windows/i.test(ua) ? 'windows'
            : /Android/i.test(ua) ? 'android'
            : /CrOS/i.test(ua) ? 'chromeos'
            : (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) ? 'ios'
            : /Macintosh/i.test(ua) ? 'macos'
            : /Linux/i.test(ua) ? 'linux' : 'other';
        card.setAttribute('data-os', os);

        /* The report is a Windows file. On Windows the way to it is open; on
           anything else the page says where THAT system shows wear, and the
           reader is one click away for a report brought from another machine. */
        var flow = $('bcReportFlow'), elsewhere = $('bcHealthOs');
        if (os !== 'windows') {
            flow.open = false;
            var line = T['health_os_' + os];
            if (line) { elsewhere.textContent = line; elsewhere.hidden = false; }
        }

        var copied = false;
        Array.prototype.forEach.call(card.querySelectorAll('[data-copy]'), function (btn) {
            btn.addEventListener('click', function () {
                var text = btn.getAttribute('data-copy'), label = btn.textContent;
                (window.__copyText ? window.__copyText(text) : Promise.resolve(false)).then(function (ok) {
                    if (!ok) return;
                    btn.textContent = t('copied');
                    setTimeout(function () { btn.textContent = label; }, 1500);
                    if (!copied) { copied = true; funnel('report_command_copied', { os: os }); }
                });
            });
        });

        var out = {
            box: $('bcReportResult'), error: $('bcReportError'),
            pct: $('bcHrPct'), band: $('bcHrBand'), facts: $('bcHrFacts'), basis: $('bcHrBasis'),
            falls: $('bcHrFalls'), fallsTitle: $('bcHrFallsTitle'), fallsList: $('bcHrFallsList'), fallsText: $('bcHrFallsText'),
            history: $('bcHrHistory'), canvas: $('bcHrCanvas'), relearns: $('bcHrRelearns'), advice: $('bcHrAdvice'),
        };
        var shown = null;

        function fact(label, value) {
            var dt = document.createElement('dt'); dt.textContent = label;
            var dd = document.createElement('dd'); dd.textContent = value;
            out.facts.appendChild(dt); out.facts.appendChild(dd);
        }

        function drawHistory(series, band) {
            var dpr = window.devicePixelRatio || 1;
            var rect = out.canvas.parentElement.getBoundingClientRect();
            var W = rect.width, H = rect.height;
            if (!(W > 0 && H > 0)) return;
            out.canvas.width = W * dpr; out.canvas.height = H * dpr;
            var ctx = out.canvas.getContext('2d'); ctx.scale(dpr, dpr);
            var pad = { top: 10, right: 12, bottom: 24, left: 40 };
            var gW = W - pad.left - pad.right, gH = H - pad.top - pad.bottom;
            var top = 100;
            series.forEach(function (s) { if (s.pct > top) top = s.pct; });
            top = Math.ceil(top / 10) * 10;
            var t0 = series[0].t, t1 = series[series.length - 1].t, dt = t1 - t0 || 1, p, y;
            ctx.strokeStyle = 'rgba(128,128,128,.15)'; ctx.lineWidth = 1;
            ctx.font = '10px system-ui'; ctx.fillStyle = 'rgba(128,128,128,.7)'; ctx.textAlign = 'right';
            for (p = 0; p <= top; p += 20) {
                y = pad.top + gH - (p / top) * gH;
                ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + gW, y); ctx.stroke();
                ctx.fillText(p + '%', pad.left - 4, y + 3);
            }
            /* Months and years for a history of years, days for one of weeks. */
            var fmt = dt > 120 * 86400000 ? { month: 'short', year: '2-digit', timeZone: 'UTC' } : { day: 'numeric', month: 'short', timeZone: 'UTC' };
            [0, 0.5, 1].forEach(function (k) {
                var when = new Date(t0 + dt * k), label;
                try { label = when.toLocaleDateString(LOCALE, fmt); }
                catch (e) { label = when.toISOString().slice(0, 10); }
                ctx.textAlign = k === 0 ? 'left' : k === 1 ? 'right' : 'center';
                ctx.fillText(label, pad.left + gW * k, H - 4);
            });
            var line = band === 'worn' ? '#ef4444' : band === 'aged' ? '#eab308' : '#22c55e';
            var X = function (s) { return pad.left + ((s.t - t0) / dt) * gW; };
            var Y = function (s) { return pad.top + gH - (Math.max(0, s.pct) / top) * gH; };
            ctx.beginPath(); ctx.strokeStyle = line; ctx.lineWidth = 2.5; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
            series.forEach(function (s, k) { if (k) ctx.lineTo(X(s), Y(s)); else ctx.moveTo(X(s), Y(s)); });
            ctx.stroke();
            if (series.length <= 60) {
                ctx.fillStyle = line;
                series.forEach(function (s) { ctx.beginPath(); ctx.arc(X(s), Y(s), 2.5, 0, Math.PI * 2); ctx.fill(); });
            }
        }

        function paint(a) {
            var days = a.span ? Math.max(1, Math.round((a.span.to - a.span.from) / 86400000)) : 0;
            var onBattery = span(a.onBatterySeconds);

            /* A pack whose reading has fallen faster than it can discharge is not
               "in good condition" on the strength of a capacity figure that comes
               from the same gauge — the corpus has one at 100 % that went from
               80 % to 1 % in four minutes. The falls outrank the band. */
            var shaky = a.falls.length > 0 && a.band !== 'worn';
            out.pct.textContent = a.healthPct == null ? '—'
                : (a.reference.basis === 'history' ? '≤ ' : '') + Math.min(100, Math.round(a.healthPct)) + '%';
            out.band.textContent = shaky ? t('band_shaky') : t('band_' + (a.band || 'unknown'));
            out.band.setAttribute('data-band', shaky ? 'worn' : (a.band || 'unknown'));
            out.pct.setAttribute('data-band', shaky ? 'unknown' : (a.band || 'unknown'));

            out.facts.textContent = '';
            if (a.fullMwh) fact(t('fact_full'), wh(a.fullMwh));
            if (a.reference) fact(a.reference.basis === 'history' ? t('fact_design_history') : t('fact_design'), wh(a.reference.mwh));
            else if (a.designMwh) fact(t('fact_design'), wh(a.designMwh));
            fact(t('fact_cycles'), a.cycles != null ? String(a.cycles) : t('fact_not_reported'));
            if (days) fact(t('fact_log'), a.onBatterySeconds > 0 ? t('fact_log_value', { time: onBattery, days: days }) : t('fact_log_none', { days: days }));

            var basis = a.reference == null ? t('basis_unknown')
                : a.reference.basis === 'history' ? t('basis_history') : '';
            if (a.batteries > 1) basis = (basis ? basis + ' ' : '') + t('two_packs');
            out.basis.textContent = basis; out.basis.hidden = !basis;

            out.fallsList.textContent = '';
            var thin = a.onBatterySeconds < 30 * 60;
            if (!a.fallsJudged) {
                out.falls.hidden = true;
            } else if (a.falls.length) {
                out.falls.hidden = false;
                out.falls.setAttribute('data-found', '1');
                out.fallsTitle.textContent = t('falls_title', { n: a.falls.length });
                a.falls.slice().reverse().slice(0, 5).forEach(function (f) {
                    var li = document.createElement('li');
                    li.textContent = t('falls_item', { date: reportDay(f.from), clock: reportClock(f.from), from: Math.round(f.fromPct), to: Math.round(f.toPct), time: span(f.seconds) });
                    out.fallsList.appendChild(li);
                });
                if (a.falls.length > 5) {
                    var more = document.createElement('li');
                    more.textContent = t('falls_more', { n: a.falls.length - 5 });
                    out.fallsList.appendChild(more);
                }
                out.fallsText.textContent = t('falls_explain') + ' ' + t('falls_capacity_note');
            } else {
                out.falls.hidden = false;
                out.falls.setAttribute('data-found', '0');
                out.fallsTitle.textContent = t('falls_title', { n: 0 });
                out.fallsText.textContent = !(a.onBatterySeconds > 0) ? t('falls_never')
                    : thin ? t('falls_thin', { time: onBattery }) : t('falls_none', { time: onBattery, days: days });
            }

            out.relearns.textContent = '';
            a.relearns.slice(-3).forEach(function (r) {
                var li = document.createElement('li');
                li.textContent = t('relearn_item', { from: Math.round(r.fromPct), to: Math.round(r.toPct), date: reportDay(r.t) });
                out.relearns.appendChild(li);
            });
            /* Two periods are a fortnight, and a line through them says nothing. */
            out.history.hidden = a.history.length < 3;

            var advice = [];
            if (a.falls.length) advice.push(t('advice_falls'));
            if (a.band && (!a.falls.length || a.band === 'worn')) advice.push(t('advice_' + a.band));
            out.advice.textContent = advice.join(' ');
            out.advice.hidden = !advice.length;

            out.box.hidden = false;
            /* A canvas in a hidden box has no size to draw into: after it shows. */
            if (!out.history.hidden) requestAnimationFrame(function () { drawHistory(a.history, a.band); });
            shown = a;
        }

        function refuse(reason) {
            out.box.hidden = true; shown = null;
            out.error.textContent = t(reason === 'no_battery' ? 'err_no_battery' : reason === 'not_a_report' ? 'err_not_report' : 'err_read');
            out.error.hidden = false;
            blocked({ reason: 'report_' + reason });
        }

        function read(file) {
            out.error.hidden = true;
            if (!file) return;
            Promise.all([whenGlobal('BatteryReport'), file.text()]).then(function (got) {
                var BR = got[0], parsed;
                try { parsed = BR.parse(got[1]); }
                catch (e) { refuse(e && e.code ? e.code : 'read'); return; }
                var a = BR.analyze(parsed);
                paint(a);
                funnel('report_read', {
                    fmt: parsed.format, band: a.band || 'unknown', falls: a.falls.length,
                    mirrored: a.mirrored ? 1 : 0, packs: a.batteries, on_battery_min: Math.round(a.onBatterySeconds / 60),
                });
            }, function () { refuse('read'); });
        }

        /* Through the shared door: it also delivers a file picked before this
           script attached (a slow connection, an eager hand). */
        window.__bindUploaderFile(drop, function (file) { read(file); });
        drop.addEventListener('tool-uploader:clear', function () {
            out.box.hidden = true; out.error.hidden = true; shown = null;
        });
        window.addEventListener('resize', function () {
            if (shown && !out.history.hidden) drawHistory(shown.history, shown.band);
        });
    }

    function start() { live(); report(); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
})();
